/**
 * Cart Drawer Recommendations Web Component
 * Loads smart recommendations based on cart items with automatic fallback,
 * wholesale pricing support, and 1-click AJAX add.
 */
if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', class CartDrawerRecommendations extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.isLoading = false;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.onDrawerShowListener = null;
      this.onResizeListener = null;
      this.scrollTimeout = null;
    }

    connectedCallback() {
      this.track = this.querySelector('.cart-rec__track');
      this.prevBtn = this.querySelector('.cart-rec__nav-prev');
      this.nextBtn = this.querySelector('.cart-rec__nav-next');

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.scrollTrack(-1);
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.scrollTrack(1);
        });
      }

      if (this.track) {
        this.track.addEventListener(
          'scroll',
          () => {
            if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
            this.scrollTimeout = setTimeout(() => this.updateNavButtons(), 40);
          },
          { passive: true }
        );
      }

      // Observar cambios de tamaño (cuando el drawer se abre y adquiere anchura real)
      if (window.ResizeObserver && this.track) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateNavButtons();
        });
        this.resizeObserver.observe(this.track);
      }

      // Observar cuando entra al viewport
      if (window.IntersectionObserver) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          if (entries[0] && entries[0].isIntersecting) {
            this.updateNavButtons();
          }
        });
        this.intersectionObserver.observe(this);
      }

      // Eventos globales de apertura del carrito
      this.onDrawerShowListener = () => {
        setTimeout(() => this.updateNavButtons(), 80);
        setTimeout(() => this.updateNavButtons(), 300);
      };
      document.addEventListener('drawer:afterShow', this.onDrawerShowListener);
      document.addEventListener('cart:open', this.onDrawerShowListener);

      this.onResizeListener = () => this.updateNavButtons();
      window.addEventListener('resize', this.onResizeListener);

      this.loadRecommendations();
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      if (this.onDrawerShowListener) {
        document.removeEventListener('drawer:afterShow', this.onDrawerShowListener);
        document.removeEventListener('cart:open', this.onDrawerShowListener);
        this.onDrawerShowListener = null;
      }
      if (this.onResizeListener) {
        window.removeEventListener('resize', this.onResizeListener);
        this.onResizeListener = null;
      }
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = null;
      }
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get cartIds() {
      const raw = this.getAttribute('data-cart-ids') || '';
      return raw.split(',').map(id => id.trim()).filter(Boolean);
    }

    get limit() {
      const val = parseInt(this.getAttribute('data-limit') || '10', 10);
      return isNaN(val) || val < 10 ? 10 : val;
    }

    get isMayorista() {
      return this.getAttribute('data-is-mayorista') === 'true';
    }

    get factorMayorista() {
      return parseFloat(this.getAttribute('data-factor-mayorista') || '1');
    }

    get recApiUrl() {
      return this.getAttribute('data-rec-url') || '/recommendations/products.json';
    }

    get fallbackApiUrl() {
      return this.getAttribute('data-fallback-url') || '/collections/all/products.json';
    }

    normalizeToCents(priceVal) {
      if (typeof priceVal === 'string') {
        if (priceVal.includes('.')) {
          return Math.round(parseFloat(priceVal) * 100);
        }
        return parseInt(priceVal, 10);
      }
      if (typeof priceVal === 'number') {
        return Math.round(priceVal);
      }
      return 0;
    }

    formatMoney(cents) {
      try {
        if (window.theme && window.theme.settings && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
          return window.theme.Currency.formatMoney(cents);
        }
      } catch (e) {
        // Ignorar y usar fallback seguro
      }

      const amount = Math.round(Number(cents) / 100);
      if (isNaN(amount)) return '$0';
      return '$' + amount.toLocaleString('en-US');
    }

    async loadRecommendations() {
      if (this.isLoading) return;
      this.isLoading = true;

      let products = [];
      const currentCartIds = this.cartIds.map(id => String(id));
      const targetLimit = this.limit;

      try {
        if (this.productId) {
          // 1. Intentar con API de recomendaciones nativa de Shopify con intent=related
          const recUrl = `${this.recApiUrl}?product_id=${encodeURIComponent(this.productId)}&limit=10&intent=related`;
          try {
            const res = await fetch(recUrl);
            if (res.ok) {
              const data = await res.json();
              if (data && Array.isArray(data.products)) {
                products = data.products.filter(p => {
                  if (!p || p.available === false) return false;
                  const idStr = String(p.id);
                  return !currentCartIds.includes(idStr);
                });
              }
            }
          } catch (e) {
            console.warn('[CartDrawerRecommendations] Error en recommendations API:', e);
          }
        }

        // 2. Si hay menos de targetLimit productos, autocompletar con productos de la colección de respaldo
        if (products.length < targetLimit) {
          const existingIds = new Set(products.map(p => String(p.id)));

          // Intento A: Cargar desde la URL de fallback configurada (colección de respaldo o /collections/all)
          try {
            const fallbackUrl = `${this.fallbackApiUrl}?limit=30`;
            const fallbackRes = await fetch(fallbackUrl);
            if (fallbackRes.ok) {
              const fallbackData = await fallbackRes.json();
              if (fallbackData && Array.isArray(fallbackData.products)) {
                for (const p of fallbackData.products) {
                  if (!p) continue;
                  const idStr = String(p.id);
                  if (currentCartIds.includes(idStr) || existingIds.has(idStr)) continue;
                  const isAvailable = p.available !== false && (!p.variants || p.variants.some(v => v.available));
                  if (!isAvailable) continue;

                  products.push(p);
                  existingIds.add(idStr);
                  if (products.length >= targetLimit) break;
                }
              }
            }
          } catch (e) {
            console.warn('[CartDrawerRecommendations] Error en fallback collection:', e);
          }

          // Intento B: Si aún faltan productos para alcanzar targetLimit, consultar /collections/all/products.json
          if (products.length < targetLimit && !this.fallbackApiUrl.includes('/collections/all/')) {
            try {
              const allRes = await fetch('/collections/all/products.json?limit=30');
              if (allRes.ok) {
                const allData = await allRes.json();
                if (allData && Array.isArray(allData.products)) {
                  for (const p of allData.products) {
                    if (!p) continue;
                    const idStr = String(p.id);
                    if (currentCartIds.includes(idStr) || existingIds.has(idStr)) continue;
                    const isAvailable = p.available !== false && (!p.variants || p.variants.some(v => v.available));
                    if (!isAvailable) continue;

                    products.push(p);
                    existingIds.add(idStr);
                    if (products.length >= targetLimit) break;
                  }
                }
              }
            } catch (e) {
              console.warn('[CartDrawerRecommendations] Error en secondary fallback:', e);
            }
          }
        }

        // Limitar a targetLimit
        products = products.slice(0, targetLimit);

        if (products.length > 0) {
          this.style.display = 'block';
          this.renderProducts(products);
        } else {
          this.style.display = 'none';
        }
      } catch (err) {
        console.error('[CartDrawerRecommendations] Error general al cargar productos:', err);
        this.style.display = 'none';
      } finally {
        this.isLoading = false;
      }
    }

    renderProducts(products) {
      if (!this.track) return;
      this.track.innerHTML = '';

      const isMayorista = this.isMayorista;
      const factorMayorista = this.factorMayorista;

      products.forEach(product => {
        const variants = product.variants || [];
        const availableVariant = variants.find(v => v.available) || variants[0];
        if (!availableVariant) return;

        const card = document.createElement('div');
        card.className = 'cart-rec__card';

        const imageSrc = product.featured_image || (product.images && product.images[0] ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src) : '');
        const productUrl = product.url || ('/products/' + (product.handle || ''));

        const rawOriginalPrice = availableVariant.price;
        const rawCompareAtPrice = availableVariant.compare_at_price;
        const originalPriceCents = this.normalizeToCents(rawOriginalPrice);
        const compareAtPriceCents = rawCompareAtPrice ? this.normalizeToCents(rawCompareAtPrice) : 0;

        let priceHtml = '';
        if (isMayorista) {
          const wholesalePriceCents = Math.round(originalPriceCents * factorMayorista);
          priceHtml = `
            <span class="cart-rec__price-orig">${this.formatMoney(originalPriceCents)}</span>
            <span class="cart-rec__price">${this.formatMoney(wholesalePriceCents)}</span>
            <span class="cart-rec__badge-wholesale">MAYORISTA</span>
          `;
        } else if (compareAtPriceCents && compareAtPriceCents > originalPriceCents) {
          priceHtml = `
            <span class="cart-rec__price-orig">${this.formatMoney(compareAtPriceCents)}</span>
            <span class="cart-rec__price">${this.formatMoney(originalPriceCents)}</span>
          `;
        } else {
          priceHtml = `<span class="cart-rec__price">${this.formatMoney(originalPriceCents)}</span>`;
        }

        card.innerHTML = `
          <a href="${productUrl}" class="cart-rec__card-media" tabindex="-1">
            ${imageSrc ? `<img src="${imageSrc}" alt="${(product.title || '').replace(/"/g, '&quot;')}" class="cart-rec__card-img" loading="lazy" width="56" height="56" />` : ''}
          </a>
          <div class="cart-rec__card-info">
            <a href="${productUrl}" class="cart-rec__card-title" title="${(product.title || '').replace(/"/g, '&quot;')}">${product.title || ''}</a>
            <div class="cart-rec__card-pricing">${priceHtml}</div>
          </div>
          <button
            type="button"
            class="cart-rec__add-btn"
            data-variant-id="${availableVariant.id}"
            aria-label="Agregar ${(product.title || '').replace(/"/g, '&quot;')} al carrito"
            title="Agregar al carrito"
          >
            <svg class="cart-rec__plus-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        `;

        const addBtn = card.querySelector('.cart-rec__add-btn');
        if (addBtn) {
          addBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleAddToCart(availableVariant.id, addBtn);
          });
        }

        this.track.appendChild(card);
      });

      this.updateNavButtons();
      setTimeout(() => this.updateNavButtons(), 60);
      setTimeout(() => this.updateNavButtons(), 200);
      setTimeout(() => this.updateNavButtons(), 500);
    }

    async handleAddToCart(variantId, btn) {
      if (!variantId || btn.hasAttribute('disabled')) return;

      btn.setAttribute('disabled', 'true');
      const originalHtml = btn.innerHTML;

      btn.innerHTML = `
        <svg class="cart-rec__spinner" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
        </svg>
      `;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });

        if (res.ok) {
          btn.classList.add('is-success');
          btn.innerHTML = `
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          `;

          // Refrescar el carrito drawer
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));

          if (window.theme && window.theme.pubsub) {
            window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-drawer-recommendations',
              productVariantId: variantId
            });
          }
        } else {
          const errData = await res.json();
          alert(errData.description || 'No se pudo agregar el producto');
          btn.innerHTML = originalHtml;
          btn.removeAttribute('disabled');
        }
      } catch (err) {
        console.error('[CartDrawerRecommendations] Error al agregar al carrito:', err);
        btn.innerHTML = originalHtml;
        btn.removeAttribute('disabled');
      }
    }

    scrollTrack(direction) {
      if (!this.track) return;
      const card = this.track.querySelector('.cart-rec__card');
      const cardWidth = card ? (card.offsetWidth + 10) : 230;
      const clientWidth = this.track.clientWidth;

      // Desplazamiento ágil y visible: avanza 2 tarjetas (~460px) en pantallas amplias o 1 tarjeta en móvil
      const scrollStep = clientWidth > 420 ? Math.min(cardWidth * 2, clientWidth * 0.85) : cardWidth;
      const scrollAmount = Math.round(scrollStep) * direction;

      if (typeof this.track.scrollBy === 'function') {
        try {
          this.track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        } catch (e) {
          this.track.scrollLeft += scrollAmount;
        }
      } else {
        this.track.scrollLeft += scrollAmount;
      }

      [40, 100, 200, 350, 500, 800].forEach((delay) => {
        setTimeout(() => this.updateNavButtons(), delay);
      });
    }

    updateNavButtons() {
      if (!this.track || !this.prevBtn || !this.nextBtn) return;

      const cards = this.track.querySelectorAll('.cart-rec__card');
      if (cards.length === 0) {
        this.prevBtn.disabled = true;
        this.nextBtn.disabled = true;
        return;
      }

      const clientWidth = this.track.clientWidth;
      const scrollWidth = this.track.scrollWidth;

      // Si el drawer está oculto o en animación (ancho 0), mantener el botón siguiente habilitado si hay varias tarjetas
      if (clientWidth <= 0 || scrollWidth <= 0) {
        this.prevBtn.disabled = true;
        this.nextBtn.disabled = cards.length <= 1;
        return;
      }

      const maxScroll = Math.max(0, scrollWidth - clientWidth);

      if (maxScroll <= 5) {
        this.prevBtn.disabled = true;
        this.nextBtn.disabled = true;
      } else {
        const scrollLeft = Math.round(this.track.scrollLeft);
        this.prevBtn.disabled = scrollLeft <= 4;
        this.nextBtn.disabled = scrollLeft >= maxScroll - 6;
      }
    }
  });
}
